import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { ArrowLeft, Edit3, MoreHorizontal, Share2, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import "./MasterPost.css";
import { BaseService } from "../../Base/BaseService";
import { Button } from "../../GloablComponents/Button";
import { BottomSheet } from "../../GloablComponents/BottomSheet/BottomSheet";
import { MasterPostStore } from "./MasterPostStore";
import { globalData } from "../../globals";

export const MasterPostPage = observer(() => {
  const store = useMemo(() => new MasterPostStore(), []);
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    const id = Number(params.id);

    if (id) {
      void store.load(id);
    }
  }, [params.id, store]);

  if (store.IsLoading) {
    return <div className="master-post-message">Загружаем пост...</div>;
  }

  if (store.Error) {
    return <div className="master-post-message error">{store.Error}</div>;
  }

  if (!store.Post) {
    return null;
  }

  const post = store.Post;

  const imageUrl = BaseService.GetFileUrl(post.imageUrl);

  const authorName = globalData.CurrentUser?.name?.trim() || "Мастер";

  const avatarLetter = authorName[0]?.toUpperCase() || "М";

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: post.description || post.title,
          url: window.location.href,
        });

        return;
      }

      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // Пользователь мог закрыть системное окно Share.
    }
  };

  return (
    <main className="master-post-page">
      <section className="master-post-hero">
        <img src={imageUrl} alt={post.title} fetchPriority="high" decoding="async" />

        <button type="button" className="master-back-button" onClick={() => navigate(-1)} aria-label="Назад">
          <ArrowLeft size={24} />
        </button>
      </section>

      <section className="master-post-content">
        <h1>{post.title}</h1>

        {post.description && <p>{post.description}</p>}

        <div className="master-post-author">
          <div className="master-post-avatar">{avatarLetter}</div>

          <div className="master-post-author-info">
            <strong>{authorName}</strong>
            <span>Публикация в портфолио</span>
          </div>

          <button type="button" className="master-more-button" onClick={() => store.openEditSheet()} aria-label="Действия с постом">
            <MoreHorizontal size={24} />
          </button>
        </div>
      </section>

      <BottomSheet isOpen={store.IsEditSheetOpen} onClose={() => store.closeEditSheet()}>
        <h2 className="sheet-title">Действия с постом</h2>

        <div className="master-sheet-actions">
          <button type="button" onClick={() => navigate(`/master/posts/${post.id}/edit`)}>
            <Edit3 size={20} />
            <span>Редактировать</span>
          </button>

          <button type="button" onClick={handleShare}>
            <Share2 size={20} />
            <span>Поделиться</span>
          </button>

          <button type="button" onClick={() => store.openDeleteSheet()}>
            <Trash2 size={20} />
            <span>Удалить</span>
          </button>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={store.IsDeleteSheetOpen} onClose={() => store.closeDeleteSheet()} className="delete-sheet">
        <h2 className="sheet-title">Удалить пост?</h2>

        <p>Пост «{post.title}» исчезнет из вашего профиля.</p>

        <div className="delete-sheet-actions">
          <Button className="delete-cancel-button" onClick={() => store.closeDeleteSheet()}>
            Отмена
          </Button>

          <Button className="sheet-button" disabled={store.IsDeleting} onClick={() => store.deletePost(() => navigate("/master/me?tab=posts"))}>
            {store.IsDeleting ? "Удаляем..." : "Удалить"}
          </Button>
        </div>
      </BottomSheet>
    </main>
  );
});
